import { useEffect } from "react";
import { Check, Ticket, Users, Crown } from "lucide-react";
import { Reveal, Badge, GlitchDivider } from "@/components/site/primitives";
import { api } from "@/lib/api";

const TIERS = [
  {
    id: "ga", icon: Ticket, name: "General Admission", price: "30", color: "#00f0ff",
    features: ["Full event access", "All stage shows & performances", "Cosplay runway spectator entry", "Comic Alley & mural battles", "Access to mocktail bar & food trucks"],
  },
  {
    id: "family", icon: Users, name: "Family 4-Pack Bundle", price: "100", color: "#ffe600", note: "Saves $20",
    features: ["Entry for 4 guests", "Everything in General Admission", "Perfect for all-ages squads", "Kids 3 & under always free"],
  },
  {
    id: "vip", icon: Crown, name: "VIP Spider-Society Pass", price: "65", color: "#ff0055", featured: true,
    features: ["Early 3PM entry", "Signed holographic Denver Spider-Verse art print", "Custom lanyard & collector badge", "2 specialty mocktail tokens", "Mezzanine VIP lounge access"],
  },
];

export default function Tickets() {
  useEffect(() => {
    const handler = (e) => {
      if (e.data && e.data.type === "eventism-widget-resize") {
        document.querySelectorAll('iframe[src*="hev_22eb140f9a9d"]').forEach((f) => {
          f.style.height = e.data.height + "px";
        });
      }
    };
    window.addEventListener("message", handler);
    api.post("/track", { type: "ticket_click" }).catch(() => {});
    return () => window.removeEventListener("message", handler);
  }, []);

  return (
    <div className="pt-24 md:pt-32">
      <section className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="text-center">
            <Badge color="magenta">Powered by Eventism</Badge>
            <h1 className="font-display text-6xl md:text-8xl uppercase mt-4">Get Your <span className="chromatic">Tickets</span></h1>
            <p className="text-white/60 font-head max-w-xl mx-auto mt-4">
              Secure your spot in the multiverse. Kids 3 &amp; under free · Student &amp; group rates available.
            </p>
          </div>
        </Reveal>
      </section>

      {/* PRICING */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {TIERS.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.1}>
              <div
                data-testid={`ticket-tier-${t.id}`}
                className={`relative rounded-3xl p-8 h-full flex flex-col ${t.featured ? "glass-chroma chromatic-box md:scale-105" : "glass"}`}
              >
                {t.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2"><Badge color="magenta">Most Popular</Badge></div>
                )}
                {t.note && (
                  <div className="absolute -top-4 right-5"><Badge color="yellow">{t.note}</Badge></div>
                )}
                <div className="w-14 h-14 rounded-full grid place-items-center border border-white/20 mb-5" style={{ background: `${t.color}22` }}>
                  <t.icon size={26} style={{ color: t.color }} />
                </div>
                <h3 className="font-display text-3xl uppercase" style={{ color: t.color }}>{t.name}</h3>
                <div className="mt-3 flex items-end gap-1">
                  <span className="font-display text-6xl text-white">${t.price}</span>
                  <span className="text-white/50 font-head mb-2">.00</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex gap-3 text-white/80 font-head text-sm">
                      <Check size={18} style={{ color: t.color }} className="shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <a
                  data-testid={`buy-${t.id}-btn`}
                  href="#eventism-widget"
                  onClick={() => api.post("/track", { type: "ticket_click" }).catch(() => {})}
                  className="mt-8 text-center font-head font-bold uppercase py-3.5 rounded-full transition-transform hover:scale-[1.03] active:scale-95"
                  style={{ background: t.color, color: t.id === "vip" ? "#fff" : "#000" }}
                >
                  Buy on Eventism
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <GlitchDivider />

      {/* EVENTISM EMBED */}
      <section id="eventism-widget" className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24 scroll-mt-24">
        <Reveal>
          <div className="text-center mb-8">
            <div className="font-mono2 text-xs tracking-[0.3em] text-[#00f0ff] uppercase mb-2">Secure Checkout</div>
            <h2 className="font-display text-4xl md:text-6xl uppercase">Reserve Now</h2>
          </div>
          <div id="eventism-ticketing-widget" data-event-id="spider-verse-denver-2026" className="glass-chroma rounded-2xl p-4 md:p-6 flex justify-center">
            <iframe
              data-testid="eventism-iframe"
              title="Get Tickets - Into the Spider-Verse Denver"
              src="https://eventism.com/embed/tickets/hev_22eb140f9a9d?accent=ff0055&bg=101018&text=ffffff&btnText=ffffff&showImage=true"
              style={{ width: "100%", maxWidth: "560px", minHeight: "620px", border: "none", borderRadius: "14px" }}
              allow="payment"
            />
          </div>
          <p className="text-center text-white/40 font-mono2 text-xs mt-4">
            Checkout securely handled by Eventism. Trouble loading? Use the tier buttons above.
          </p>
        </Reveal>
      </section>

      {/* GROUP POLICY */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
        <Reveal>
          <div className="glass rounded-2xl p-8 grid md:grid-cols-3 gap-6">
            {[
              { t: "Group Bookings", d: "Rolling with a crew of 10+? Email dondrae@eventism.com for discounted group rates." },
              { t: "Student & Youth", d: "Discounted youth pricing available at the door with valid student ID." },
              { t: "Little Spiders", d: "Kids 3 & under enter completely free — no ticket required." },
            ].map((x) => (
              <div key={x.t}>
                <h3 className="font-head font-bold text-lg text-[#ffe600]">{x.t}</h3>
                <p className="text-white/60 font-head text-sm mt-2">{x.d}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
